import React from 'react';

/**
 * Creates a referentially stable event handler that has up-to-date values upon the
 * time of invocation.
 *
 * A couple important things to note:
 * - The returned handler is **_referentially stable_**. This means it is 100% safe to
 *   use within hook dependency arrays or as a prop to `React.memo` without causing
 *   extra rerenders.
 * - Because the returned handler will not cause rerenders, it's best suited to handling
 *   _user initiated events_ (like a button click or form submission), rather than reactively
 *   running a callback based on data (whenever `X` value changes, perform `Y` side-effect using
 *   the value of X). It's not a better `React.useCallback`, and should not be considered as a 100% substitute.
 * - This is inspired by [this RFC](https://github.com/reactjs/rfcs/blob/useevent/text/0000-useevent.md)
 *   in React itself. So if / when that RFC is merged to React we can probably deprecate
 *   this hook entirely.
 */
const useEventHandler = <T extends (...args: any[]) => any>(callback: T): T => {
  const callbackRef = React.useRef<T>(callback);

  // Keep the callback ref synced with the outer callback function. Depending on how
  // the outer callback is defined this could happen every render (if an arrow function)
  // or only when the callback's reference changes (if the outer callback itself is a
  // `React.useCallback`)
  //
  // This guarantees that whenever we _do_ invoke the `callback` function it has
  // up-to-date values.
  React.useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // The handler returned from this hook calls the callback ref and returns its value.
  // However, since we've placed the callback in a `React.useRef` we can safely omit
  // all dependencies, making this `eventHandler` function a stable reference.
  const eventHandler = React.useCallback((...args: Parameters<T>) => {
    return callbackRef.current(...args) as ReturnType<T>;
  }, []);

  return eventHandler as T;
};

export default useEventHandler;
